import type { ReactElement } from 'react';
import styled from 'styled-components';

export interface DescriptionField {
  key: string;
  label: string;
  hint?: string;
}

interface Props {
  notes: Record<string, string>;
  onChange: (key: string, value: string) => void;
}

// Description fields; combined into a single Zendesk note on submit.
export const DESCRIPTION_FIELDS: DescriptionField[] = [
  { key: 'summary', label: 'Summary', hint: 'short, clear description' },
  { key: 'done', label: 'What has been done so far' },
  { key: 'findings', label: 'Findings / Observations', hint: 'logs, screenshots of Mux, timestamps' },
  { key: 'reason', label: 'Reason for status' },
  { key: 'nextSteps', label: 'Next steps', hint: 'specify owner and assign ticket to them' }
];

/*
 * Blank note per field. `buildBody` indexes the record without a guard, which is
 * only safe because every record starts here — keep the two together.
 */
export const emptyNotes = (): Record<string, string> =>
  Object.fromEntries(DESCRIPTION_FIELDS.map((field) => [field.key, '']));

/*
 * Turns the description fields into the single note body Zendesk receives:
 * one "-Label (hint): text" line per filled-in field. Empty fields are skipped
 * so the note doesn't carry a wall of blank headings.
 */
export const buildBody = (notes: Record<string, string>): string =>
  DESCRIPTION_FIELDS.filter((field) => notes[field.key].trim().length > 0)
    .map((field) => {
      const hint = field.hint === undefined ? '' : ` (${field.hint})`;

      return `-${field.label}${hint}: ${notes[field.key].trim()}`;
    })
    .join('\n');

const DescSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  .caption {
    font-weight: 600;
    font-size: 9px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(176, 186, 209, 0.5);
  }

  .top {
    margin-bottom: 5px;
    font-size: 11px;
  }

  .name {
    font-weight: 600;
    color: rgba(213, 221, 238, 0.85);
  }

  .hint {
    color: rgba(176, 186, 209, 0.5);
    font-weight: 500;
  }
`;

const NoteTextarea = styled.textarea`
  width: 100%;
  background: rgba(8, 14, 30, 0.5);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: rgba(213, 221, 238, 0.92);
  padding: 8px 11px;
  font-weight: 400;
  font-size: 12px;
  min-height: 42px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

export const DescriptionFields = ({ notes, onChange }: Props): ReactElement => (
  <DescSection>
    <div className='caption'>Description · combined into one Zendesk note</div>
    {DESCRIPTION_FIELDS.map((field) => (
      <div key={field.key}>
        <div className='top'>
          <span className='name'>{field.label}</span>
          {field.hint !== undefined && <span className='hint'> ({field.hint})</span>}
        </div>
        <NoteTextarea
          value={notes[field.key]}
          onChange={(event): void => {
            onChange(field.key, event.target.value);
          }}
        />
      </div>
    ))}
  </DescSection>
);
